import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Star as StoryIcon,
  Build as TaskTypeIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { priorities, issueTypes, statuses, getIssueTypeById } from './issuetype';
import TaskDetailsPanel from './taskdetailpanel';
import axios from 'axios';
// Make sure this is correctly configured in your environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';


const ActiveSprintBoard = ({ projectKey }) => {
  const [issuesByStatus, setIssuesByStatus] = useState({ 
    todo: [], 
    inProgress: [], 
    done: [] 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [sprintData, setSprintData] = useState({
  sprintStartDate: null,
  sprintEndDate: null,
  sprintName:null,
});
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const { projectId } = useParams(); 

 

  // Fetch active sprint issues when component mounts
  useEffect(() => {
    fetchActiveSprintIssues();
  }, []);

  const fetchActiveSprintIssues = async () => {
    try {
             const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      setError(null);
      
      
      const response = await axios.get(`${API_BASE_URL}/api/active/active-sprints/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      

      
      const data = response.data
      
      if (data && data.success) {
        setIssuesByStatus(data.issues || { todo: [], inProgress: [], done: [] });
        if (data.sprints && data.sprints.length > 0) {
        const activeSprint = data.sprints[0]; // Get the first (active) sprint
        setSprintData({
          sprintStartDate: activeSprint.startDate,
          sprintEndDate: activeSprint.endDate,
          sprintName: activeSprint.name || 'Active Sprint' // Optional: store sprint name
        });
      } else {
        // Handle case where no active sprint exists
        setSprintData({
          sprintStartDate: null,
          sprintEndDate: null,
          sprintName: null
        });
      }
      } else {
        throw new Error(data?.message || 'Unknown error occurred');
      }
    } catch (apiError) {
      setError(`Failed to fetch issues: ${apiError.message}`);
      showSnackbar(`API Error: ${apiError.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false, message: '', severity: 'info'  }));
  };

  // Update issue status in backend
  const updateIssueStatus = async (issueId, newStatus) => {
    try {
             const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_BASE_URL}/api/issues/${issueId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        showSnackbar(`Error updating issue: ${errorText}`, 'error');
        return false
      }
      
      const result = await response.json();
      
     if (result && (result.success === true || result.success === "true")) {
  showSnackbar(`Status updated successfully`, 'success');
  return true;
} else {
      showSnackbar(result?.message || 'Unknown error updating issue status', 'error');
      return false;
    }
    } catch (error) {
      showSnackbar(`Error updating issue: ${error.message}`, 'error');
      return false;
    }
  };

  const handleCardClick = (issue) => {
    setSelectedIssue(issue);
    setShowTaskPanel(true);
  };

  const handleCloseTaskPanel = () => {
    setShowTaskPanel(false);
    setSelectedIssue(null);
  };

  const handleUpdateIssue = (updatedIssue) => {
    if (updatedIssue) {
      setSelectedIssue(prev => (prev && prev.id === updatedIssue.id ? { ...prev, ...updatedIssue } : updatedIssue));
    }
    fetchActiveSprintIssues();
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };


const calculateDaysLeft = () => {
  if (!sprintData.sprintEndDate) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate calculation
  
  const endDate = new Date(sprintData.sprintEndDate);
  endDate.setHours(0, 0, 0, 0); // Reset time to start of day
  
  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

  // Drag and Drop handlers
  const handleDragStart = (e, issue, sourceColumn) => {
    setDraggedItem({ issue, sourceColumn });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, column) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedItem || draggedItem.sourceColumn === targetColumn) {
      setDraggedItem(null);
      return;
    }

    const { issue, sourceColumn } = draggedItem;
    
    // Convert column names to status numbers
    const statusMap = {
      'todo': 1,
      'inProgress': 2,
      'done': 3
    };
    
    const newStatusNumber = statusMap[targetColumn];
    
    // Optimistically update UI
    setIssuesByStatus(prev => {
      const newState = { ...prev };
      
      // Remove from source column
      newState[sourceColumn] = newState[sourceColumn].filter(i => i.id !== issue.id);
      
      // Add to target column with updated status
      const updatedIssue = { ...issue, status: newStatusNumber };
      newState[targetColumn] = [...newState[targetColumn], updatedIssue];
      
      return newState;
    });
    
    // Update backend
    const success = await updateIssueStatus(issue.id, newStatusNumber);
    
    // If backend update failed, revert the UI change
    // if (!success) {
    //   setIssuesByStatus(prev => {
    //     const revertState = { ...prev };
        
    //     // Remove from target column
    //     revertState[targetColumn] = revertState[targetColumn].filter(i => i.id !== issue.id);
        
    //     // Add back to source column
    //     revertState[sourceColumn] = [...revertState[sourceColumn], issue];
        
    //     return revertState;
    //   });
    // }
    if(!success){
      fetchActiveSprintIssues();
    }
    
    setDraggedItem(null);
  };

  const getStatusDisplayName = (statusNumber) => {
    switch (statusNumber) {
      case 1:
        return 'To Do';
      case 2:
        return 'In Progress';
      case 3:
        return 'Done';
      default:
        return 'Unknown';
    }
  };
const getIssueTypeIcon = (typeId) => {
  const issueType = getIssueTypeById(typeId);
  if (!issueType) return <TaskTypeIcon fontSize="small" color="primary" />;
  
  return (
    <Tooltip title={issueType.name} arrow>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {issueType.icon}
      </span>
    </Tooltip>
  );
};
  


  const getPriorityColor = (priority) => {
  if (!priority) return 'default';
  const priorityObj = priorities.find(p => p.id === parseInt(priority));
  return priorityObj ? priorityObj.name : priority;
};

 const getPriorityColorValue = (priority) => {
  if (!priority) return 'default';
  switch (String(priority).toLowerCase()) {
    case '1':
    case 'high':
    case 'critical':
      return 'error';
    case '2':
    case 'medium':
      return 'warning';
    case '3':
    case 'low':
      return 'success';
    default:
      return 'default';
  }
};

  const renderIssueCard = (issue, column) => {
    return (
      <Card 
        key={issue.id}
        draggable
        onDragStart={(e) => handleDragStart(e, issue, column)}
        onClick={() => handleCardClick(issue)}
        sx={{ 
          mb: 2, 
          cursor: 'pointer',
          '&:hover': { 
            boxShadow: 3,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out'
          },
          '&:active': {
            cursor: 'grabbing'
          },
          opacity: draggedItem?.issue.id === issue.id ? 0.5 : 1
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header with drag indicator and issue type */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* <DragIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: '1rem' }} /> */}
              {/* {getIssueTypeIcon(issue.type)} */}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {issue.key || `ISS-${issue.id}`}
              </Typography>
            </Box>
            {issue.priority && (
              <Chip 
                label={getPriorityColor(issue.priority)}  // Shows name instead of number
                color={getPriorityColorValue(issue.priority)}  // For color
                size="small"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            )}
          </Box>
          
          {/* Issue title */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500, 
              mb: 1.5, 
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {issue.title || issue.summary || 'Untitled Issue'}
          </Typography>

          {/* Issue description */}
          {issue.description && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mb: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.2
              }}
            >
              {issue.description}
            </Typography>
          )}

          {/* Footer with assignee and estimate */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {issue.assignee && (
                <Tooltip title={issue.assigneeName}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', mr: 1 }}>
                    {issue.assigneeName.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
  {getIssueTypeIcon(issue.issueType)} {/* Show icon with tooltip */}
  {/* <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
    {issue.key || `ISS-${issue.id}`}
  </Typography> */}
</Box>

              
            </Box>
            
            {issue.labels && issue.labels.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {issue.labels.slice(0, 2).map((label, index) => (
                  <Chip 
                    key={index}
                    label={label}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: '18px' }}
                  />
                ))}
                {issue.labels.length > 2 && (
                  <Typography variant="caption" color="text.secondary">
                    +{issue.labels.length - 2}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderColumn = (title, issues, columnKey, color) => (
    <Paper 
      sx={{ 
        p: 2, 
        minHeight: '600px',
        bgcolor: dragOverColumn === columnKey ? 'action.hover' : 'grey.50',
        borderTop: `3px solid`,
        borderTopColor: color,
        transition: 'background-color 0.2s ease-in-out'
      }}
      onDragOver={(e) => handleDragOver(e, columnKey)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, columnKey)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Chip 
          label={issues.length} 
          size="small" 
          sx={{ 
            bgcolor: color, 
            color: 'white',
            fontWeight: 600
          }} 
        />
      </Box>
      
      <Box sx={{ maxHeight: '520px', overflowY: 'auto' }}>
        {issues.length > 0 ? (
          issues.map(issue => renderIssueCard(issue, columnKey))
        ) : (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 4,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: dragOverColumn === columnKey ? 'action.selected' : 'transparent'
            }}
          >
            <TaskIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
  No issues in {title.toLowerCase().replace("in progress", "progress")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Drag issues here to update their status
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
            Active Sprint 
          </Typography>
           {/* <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
      {sprintData.sprintName || 'Active Sprint'}
      {calculateDaysLeft() !== null && (
        <Chip 
          label={`${calculateDaysLeft()} days left`}
          color={calculateDaysLeft() <= 3 ? 'error' : calculateDaysLeft() <= 7 ? 'warning' : 'success'}
          sx={{ ml: 2 }}
        />
      )}
    </Typography> */}
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Drag and drop issues to update their status
          </Typography>
         {sprintData.sprintStartDate && sprintData.sprintEndDate && (
      <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Start:</strong> {new Date(sprintData.sprintStartDate).toLocaleDateString()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>End:</strong> {new Date(sprintData.sprintEndDate).toLocaleDateString()}
        </Typography>
        <Chip 
          label={`Sprint Duration: ${Math.ceil((new Date(sprintData.sprintEndDate) - new Date(sprintData.sprintStartDate)) / (1000 * 60 * 60 * 24))} days`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
          color='primary'
        />
      </Box>
    )}
  </Box>
        
        <IconButton
          color="primary"
          onClick={fetchActiveSprintIssues}
          disabled={loading}
          sx={{ 
            bgcolor: 'primary.light', 
            color: 'white',
            '&:hover': { bgcolor: 'primary.main' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={fetchActiveSprintIssues}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {renderColumn('TO DO', issuesByStatus.todo, 'todo', '#6c757d')}
          </Grid>
          
          <Grid item xs={12} md={4}>
            {renderColumn('IN PROGRESS', issuesByStatus.inProgress, 'inProgress', '#ffc107')}
          </Grid>
          
          <Grid item xs={12} md={4}>
            {renderColumn('DONE', issuesByStatus.done, 'done', '#28a745')}
          </Grid>
        </Grid>
      )}

      {/* Summary Statistics */}
      <Paper sx={{ mt: 3, p: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {issuesByStatus.todo.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              To Do
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              {issuesByStatus.inProgress.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              In Progress
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {issuesByStatus.done.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Done
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary.main">
              {issuesByStatus.todo.length + issuesByStatus.inProgress.length + issuesByStatus.done.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Issues
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Task Details Panel */}
      {showTaskPanel && selectedIssue && (
        <>
          {/* Backdrop/Overlay */}
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
              backdropFilter: "blur(2px)",
            }}
            onClick={handleCloseTaskPanel}
          />
          <TaskDetailsPanel
            issue={selectedIssue}
            onClose={handleCloseTaskPanel}
            onUpdate={handleUpdateIssue}
            issueTypes={issueTypes || [
              { id: 1, name: 'Bug' },
              { id: 2, name: 'Epic' }, 
              { id: 3, name: 'Story' },
              { id: 4, name: 'Task' }
            ]}
            statuses={statuses || [
              { id: 1, name: 'To Do' },
              { id: 2, name: 'In Progress' },
              { id: 3, name: 'Done' },
            ]}
            priorities={priorities || [
              { id: 1, name: 'Highest' },
              { id: 2, name: 'High' },
              { id: 3, name: 'Medium' },
              { id: 4, name: 'Low' },
              { id: 5, name: 'Lowest' }
            ]}
            getInitials={getInitials}
          />
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActiveSprintBoard;